// 获取视频信息
import {makeObservable, observable, computed, toJS, action, flow} from 'mobx';

export class VideoStore {
    videoUrl = '';

    // canvas时间线
    frameIndex = 0;

    // 视频总时长
    duration = 0;

    // 当前帧
    secondsPerFrame = 0;

    // 总帧数
    frameCount = 0;// 18//770

    // 原始视频宽度
    originWidth = 0;

    // 原始视频高度
    originHeight = 0;

    // 滑条距离
    currentFrameX = 0;

    // 左边裁剪距离
    leftBarX = 0;

    // 右边裁剪距离
    rightBarX = 0;

    // 记录上次的指针位置
    pointerPreX = 0;

    // 操作状态
    currentStatus = '';

    // 标记是否正在播放
    isPlaying = false;

    // 当前时间
    currentTime = 0;

    // 视频
    videoRef = null;

    // 该视频用了绘制帧的canvas
    shadowVideoRef = null;

    // 当前裁剪任务
    clipTask = {};

    // canvas帧
    // @observable frameRefs = [];

    constructor() {
        makeObservable(this, {
            videoUrl: observable,
            frameIndex: observable,
            duration: observable,
            secondsPerFrame: observable,
            frameCount: observable,
            originWidth: observable,
            originHeight: observable,
            currentFrameX: observable,
            leftBarX: observable,
            rightBarX: observable,
            pointerPreX: observable,
            currentStatus: observable,
            isPlaying: observable,
            currentTime: observable,
            videoRef: observable,
            shadowVideoRef: observable,
            clipTask: observable,

            progressWidth: computed,
            clipWidth: computed,
            getCurrentDuration: computed,
            clipStart: computed,
            clipEnd: computed,
            getShowTimePopoverLeftX: computed,
            getTimeFormatValue: computed,

            loadVideo: action,
            initVideo: action,
            drawAllFrames: action,
            drawVideoFrame: action,
            tryDraw: action,
            onMove: action,
            needleMove: action,
            leftBarMove: action,
            rightBarMove: action,
            showVideoTime: action,
            toggleVideoPlay: action,
            onVideoTimeUpdate: action,
            formatTime: action,
            formatCurrentTime: action,
            getTimebyX: action,
            getXByTime: action,
            setClipTask: action
        });
    }
    get progressWidth() {
        return Math.floor((this.originWidth / this.originHeight) * 76) * this.frameCount;
    }

    // 裁剪宽度
    get clipWidth() {
        const width = this.rightBarX - this.leftBarX;
        return width;
    }

    // 裁剪时长
    get getCurrentDuration() {
        return this.clipWidth / this.progressWidth * this.duration;
    }

    get clipStart() {
        return this.getTimebyX(this.leftBarX) || 0;
    }

    get clipEnd() {
        return this.getTimebyX(this.rightBarX) || 0;
    }

    // 计算显示时间组件的定位
    get getShowTimePopoverLeftX() {
        if (this.currentStatus === 'moveingLeftBar') {
            return this.leftBarX;
        }
        if (this.currentStatus === 'moveingRightBar') {
            return this.rightBarX;
        }
        if (this.currentStatus === 'moveingNeedle') {
            return this.currentFrameX;
        }
        return 0;
    }

    // 计算时间组件的值
    get getTimeFormatValue() {
        if (this.currentStatus === 'moveingLeftBar') {
            return this.formatCurrentTime(this.leftBarX);
        }
        if (this.currentStatus === 'moveingRightBar') {
            return this.formatCurrentTime(this.rightBarX);
        }
        if (this.currentStatus === 'moveingNeedle') {
            return this.formatCurrentTime(this.currentFrameX);
        }
        return 0;
    }

    loadVideo = url => {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            video.src = url;
            video.onloadedmetadata = () => {
                resolve(video);
            };
            video.onerror = () => {
                reject(new Error('video load failed'));
            };
        });
    };

    initVideo = videoDetail => {
        let {video, clipStart = 0, clipEnd} = videoDetail || {};
        let {duration = 0, videoHeight = 0, videoWidth = 0} = video || {};
        const realWidth = Math.ceil((videoWidth / videoHeight) * 76);
        const count = Math.ceil(770 / realWidth);

        this.duration = duration;
        this.originHeight = videoHeight;
        this.originWidth = videoWidth;
        this.frameCount = count;
        this.secondsPerFrame = duration / count;
        this.isPlaying = false;
        this.rightBarX = clipEnd ? this.getXByTime(clipEnd) : this.progressWidth;
        this.leftBarX = clipStart ? this.getXByTime(clipStart) : 0;
        if (this.videoRef) {
            this.videoRef.currentTime = clipStart;
        }
    };

    // 获取视频帧
    drawAllFrames = flow(function* (frameRefs, frameIndex) {
        if (frameRefs && frameIndex === 0 && frameRefs.current && frameRefs.current[frameIndex]) {// 二次打开第一帧绘制丢失的问题
            yield this.drawVideoFrame(
                this.shadowVideoRef,
                frameIndex * this.secondsPerFrame,
                frameRefs.current[frameIndex]
            );
        }
        if (frameRefs && frameRefs.current && frameRefs.current[frameIndex]) {
            yield this.drawVideoFrame(
                this.shadowVideoRef,
                frameIndex * this.secondsPerFrame,
                frameRefs.current[frameIndex]
            );
            this.drawAllFrames(frameRefs, frameIndex + 1);
        }
    });

    drawVideoFrame = (video, time, canvas) => {
        return new Promise((resolve, reject) => {
            if (video) {
                const point = video.duration - Math.floor(video.duration);

                // 始终展示第1帧和最后一帧
                if (video.duration - time < this.secondsPerFrame) {
                    video.currentTime = video.duration;
                }
                else {
                    video.currentTime = time + (point > 0.2 ? point / 2 : 0.1);
                }

                video.ontimeupdate = e => {
                    setTimeout(() => {
                        this.tryDraw(video, canvas, resolve);
                    });
                };
            }
        });
    };

    tryDraw = (video, canvas, resolve) => {
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.drawImage(
                video,
                0,
                0,
                canvas.width,
                canvas.height
            );
        }
        resolve();
    };

    onMove = e => {
        e.preventDefault();

        if (!this.currentStatus) {
            return;
        }

        switch (this.currentStatus) {
            case 'moveingNeedle':
                this.needleMove(e);
                break;
            case 'moveingLeftBar':
                this.leftBarMove(e);
                break;
            case 'moveingRightBar':
                this.rightBarMove(e);
                break;
            default:
        }
    };

    needleMove = e => {
        const delta = Math.round(e.pageX - this.pointerPreX);
        let nextX = this.currentFrameX + delta;
        // TODO: 边界判断
        if (nextX < this.leftBarX) {
            nextX = this.leftBarX;
        }
        if (nextX > this.rightBarX) {
            nextX = this.rightBarX;
        }

        this.currentFrameX = nextX;
        this.pointerPreX = e.pageX;
        this.showVideoTime();
    };

    leftBarMove = e => {
        const delta = e.pageX - this.pointerPreX;
        let nextX = this.leftBarX + delta;

        // 边界判断
        if (nextX > this.rightBarX) {
            nextX = this.rightBarX;
        }

        this.leftBarX = nextX < 0 ? 0 : nextX;
        this.pointerPreX = e.pageX;

        const startTime = this.getTimebyX(this.leftBarX);
        // 这行代码添加了之后会当leftbar移动时，needle也会跟着移动
        if (this.videoRef) {
            this.videoRef.currentTime = startTime;
        }
    };

    rightBarMove = e => {
        const delta = e.pageX - this.pointerPreX;
        let nextX = this.rightBarX + delta;

        if (nextX < this.leftBarX) {
            nextX = this.leftBarX;
        }
        if (nextX > this.progressWidth) {
            nextX = this.progressWidth;
        }

        this.rightBarX = nextX;
        if (this.currentFrameX > this.rightBarX) {
            this.currentFrameX = this.rightBarX;
        }
        this.pointerPreX = e.pageX;
    };

    // 根据关键帧的位置，实时展示当前video
    showVideoTime = () => {
        if (this.videoRef) {
            const percentage = this.currentFrameX / this.progressWidth;
            this.videoRef.currentTime = this.duration * percentage;
        }
    };

    // 预览视频
    toggleVideoPlay = () => {
        if (this.videoRef) {
            if (this.videoRef.paused) {
                const startTime = this.getTimebyX(this.leftBarX);
                const endTime = this.getTimebyX(this.rightBarX);

                if (this.videoRef.currentTime >= endTime) {
                    this.videoRef.currentTime = startTime;

                    // setTimeout(() => {
                    //     this.videoRef.play();
                    //     this.isPlaying = true;
                    // }, 500);
                }
                else {
                    this.videoRef.play();
                    this.isPlaying = true;
                }
            }
            else {
                this.videoRef.pause();
                this.isPlaying = false;
            }
        }
    };

    onVideoTimeUpdate = () => {
        if (this.videoRef && this.duration) {
            // 将当前时间转换为参数
            const currentTime = this.videoRef.currentTime;
            const percentage = currentTime / this.duration;
            const endTime = this.getTimebyX(this.rightBarX);
            this.currentFrameX = Math.floor(this.progressWidth * percentage);

            if (currentTime >= endTime) {
                this.videoRef.currentTime = endTime;
                this.videoRef.pause();
                this.isPlaying = false;
                this.currentFrameX = this.leftBarX;
                this.videoRef.currentTime = this.getTimebyX(this.leftBarX);
            }
        }
    };

    // 格式化时间
    formatTime(seconds) {
        let milliseconds = Math.round(seconds * 1000);

        if (milliseconds % 1000 >= 500) {
            milliseconds += 1000 - (milliseconds % 1000);
        }

        let hour = Math.floor(milliseconds / (1000 * 60 * 60));
        let minute = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
        let second = Math.floor((milliseconds % (1000 * 60)) / 1000);
        let millisecond = Math.floor((milliseconds % 1000) / 10);

        if (hour < 10) {
            hour = `0${hour}`;
        }
        if (minute < 10) {
            minute = `0${minute}`;
        }
        if (second < 10) {
            second = `0${second}`;
        }
        if (millisecond < 10) {
            millisecond = `0${millisecond}`;
        }
        return `${hour}:${minute}:${second}:${millisecond}`;
    }

    formatCurrentTime(X) {
        const time =  this.getTimebyX(X);
        return this.formatTime(time);
    }

    // 根据x坐标，计算当前时间
    getTimebyX(x = 0) {
        const percentage = x / this.progressWidth;
        const time = this.duration * percentage || 0;
        return time.toFixed(2);
    }

    // 根据时间，计算当前关键帧的位置
    getXByTime(time = 0) {
        const percentage = time / this.duration;
        return Math.ceil(this.progressWidth * percentage);
    }

    // 设置剪辑任务
    setClipTask(task) {
        this.clipTask = task;
    }
}
